import { useToken } from "@gluestack-style/react";
import Section from "components/general/Section";
// import { eventTypeLabels } from "app/(pages)/(inner)/(members-area)/notifications";
import { Box, FormattedDate, H3, HStack, Link, Pressable, Text, Typography } from "components/ui";
import { Ban, CircleCheck, ClipboardCheck, Ellipsis, Eye, EyeOff, Inbox, MapPin, Repeat, ShoppingCart, Truck } from "lucide-react-native";
import React from "react";
import { Col, Container as GridContainer, Row } from "react-native-flex-grid";
import { Notification, usePostCartByIdCheckoutMutation, usePostCartByIdRejectMutation, usePostNotificationMarkreadMutation, usePostNotificationMarkunreadMutation } from "store/abcoApi";
import { eventTypeLabels } from "types/constants.type";
import { truncateText } from "utils/globalFunctions";
import { useIsMobile } from "utils/hooks/useIsMobile";

type Props = {
	dataList: Notification[];
};

type MenuOptionType = {
	value: string;
	icon: JSX.Element;
	label: string;
	action: (notification: any) => void;
};

const iconMapping: { [key: string]: JSX.Element } = {
	CartWaitingForApproval: <ShoppingCart />,
	CartRejected: <ShoppingCart />,
	CartApproved: <CircleCheck />,

	ShippingInProgress: <Truck />,
	ShippingInTransit: <Truck />,
	ShippingCompleted: <Truck />,
	ShippingFailure: <Truck />,

	InvoiceCreated: <Inbox />,
	OrderConfirmation: <Inbox />,
};

const linkMapping: { [key: string]: string } = {
	CartWaitingForApproval: "/cart/",
	CartRejected: "/cart/",
	CartApproved: "/cart/",

	ShippingInProgress: "/orders/",
	ShippingInTransit: "/orders/",
	ShippingCompleted: "/orders/",
	ShippingFailure: "/orders/",

	InvoiceCreated: "/finance/invoice/",
	OrderConfirmation: "/orders/",
};

const getMenuMapping = (
	approveOrder: (notification: any) => void,
	rejectOrder: (notification: any) => void,
	repeatOrder: (notification: any) => void,
	cancelOrder: (notification: any) => void,
): {
	[key: string]: MenuOptionType[];
} => ({
	CartWaitingForApproval: [
		{ value: "Approve request", icon: <ClipboardCheck color="black" width={16} height={16} />, label: "Approve request", action: approveOrder },
		{ value: "Reject request", icon: <Ban color="black" width={16} height={16} />, label: "Reject request", action: rejectOrder },
	],
	CartRejected: [],
	CartApproved: [],
	ShippingInProgress: [],
	ShippingInTransit: [],
	ShippingCompleted: [],
	ShippingFailure: [],
	InvoiceCreated: [],
	OrderConfirmation: [
		{ value: "Repeat Order", icon: <Repeat color="black" width={16} height={16} />, label: "Repeat order", action: repeatOrder },
		{ value: "Cancel Order", icon: <Ban color="black" width={16} height={16} />, label: "Cancel order", action: cancelOrder },
	],
});

const NotificationTable = ({ dataList }: Props) => {
	const isMobile = useIsMobile();
	const resolvedPrimarylight = useToken("colors", "primarylight");
	const [notificationMarkRead] = usePostNotificationMarkreadMutation();
	const [notificationMarkUnRead] = usePostNotificationMarkunreadMutation();
	const [checkoutCart] = usePostCartByIdCheckoutMutation();
	const [rejectCart] = usePostCartByIdRejectMutation();

	const handleMarkRead = (id: number) => {
		notificationMarkRead({ idCollection: [id] }).then(() => {});
	};

	const handleMarkUnRead = (id: number) => {
		notificationMarkUnRead({ idCollection: [id] }).then(() => {});
	};

	function approveOrder(notification: any) {
		checkoutCart({ id: notification.transactionId as number }).then(() => {
			notificationMarkRead({ idCollection: [notification.id] }).then(() => {});
		});
	}

	function rejectOrder(notification: any) {
		rejectCart({ id: notification.transactionId as number }).then(() => {
			notificationMarkRead({ idCollection: [notification.id] }).then(() => {});
		});
	}

	function repeatOrder() {
		alert("Repeat order");
	}

	function cancelOrder() {
		alert("Cancel order");
	}

	const useTransactionNumberTypes = [
		"OrderConfirmation",
		"ShippingInProgress",
		"ShippingInTransit",
		"ShippingCompleted",
		"ShippingFailure",
		"InvoiceCreated",
		];

	const menuMapping = getMenuMapping(approveOrder, rejectOrder, repeatOrder, cancelOrder);

	return (
		<GridContainer
			noPadding
			style={{
				marginLeft: 0,
				maxWidth: "100%",
				marginBottom: 30,
				gap: 12,
			}}
		>
			{dataList.length === 0 && (
				<Section gap={24}>
					<Box flex={2} gap={12}>
						<H3>No Notification history.</H3>
						<Typography sx={{ lineHeight: 21 }}>This page keeps track of all your notifications history.</Typography>
						<Typography sx={{ lineHeight: 21 }}>Right now, it's empty — once notifications is added, everything will appear here automatically.</Typography>
					</Box>
				</Section>
			)}

			{dataList.length > 0 && (
				<>
					{!isMobile && (
						<Row style={{ marginLeft: 0, marginRight: 0, marginBottom: 14, width: "100%", paddingHorizontal: 16 }}>
							<Col>
								<Text>Date</Text>
							</Col>
							<Col>
								<Text>Type</Text>
							</Col>
							<Col>
								<Text>Transaction Number</Text>
							</Col>
							<Col>
								<Text>Site</Text>
							</Col>
						</Row>
					)}

					{dataList.map((notification: any) => {
						let hasReadStyle = { fontWeight: "400" };
						if (notification.hasRead === false) {
							hasReadStyle = { fontWeight: "700" };
						}

						let link = linkMapping[notification.eventType] + (
							useTransactionNumberTypes.includes(notification.eventType)
								? notification.transactionNumber
								: notification.transactionId
						);

						let eye = (
							<Pressable style={{ position: "absolute", width: 30, top: 16, left: 14, shadowOpacity: 0, zIndex: 20 }} onPress={() => handleMarkRead(notification.id)}>
								<Eye size={24} color={resolvedPrimarylight} />
							</Pressable>
						);
						if (notification.hasRead === true) {
							eye = (
								<Pressable style={{ position: "absolute", top: 16, left: 14, shadowOpacity: 0, zIndex: 20 }} onPress={() => handleMarkUnRead(notification.id)}>
									<EyeOff size={24} color={"#999999"} onPress={() => handleMarkUnRead(notification.id)} />
								</Pressable>
							);
						}

						return (
							<Box position="relative" key={notification.id}>
								{eye}
								<Link
									href={link}
									onPress={() => handleMarkRead(notification.id)}
									key={notification.id}
									component={(hovered: boolean) => {
										var styleData = {
											backgroundColor: "white",
											marginLeft: 0,
											marginRight: 0,
											paddingHorizontal: isMobile ? 0 : 16,
											paddingVertical: 12,
											borderRadius: 8,
											borderWidth: 1,
											marginBottom: 0,
											borderColor: "#E1E9F0",
										};

										if (hovered) {
											// @ts-ignore
											styleData = { ...styleData, backgroundColor: "#E1E9F0" };
										}
										if (isMobile) {
											return (
												<Row style={styleData} key={notification.transactionNumber}>
													<Col>
														<Row style={{ paddingBottom: 6 }}>
															<Col style={{ flexDirection: "row", justifyContent: "space-between" }}>
																<HStack gap={8} alignItems="center">
																	<Text sx={{ marginLeft: 32, ...hasReadStyle }}>{notification.transactionNumber}</Text>
																</HStack>
																<HStack gap={8} alignItems="center">
																	<FormattedDate sx={{ color: "$neutral400", fontSize: 14, ...hasReadStyle }} variant="DD/MMM" date={notification.dateCreated} />
																	<Box
																		opacity={0}
																		borderWidth={1}
																		borderColor="#8298AB"
																		alignItems="center"
																		borderRadius={"$full"}
																		justifyContent="center"
																		bg="#E1E9F0"
																		width={"$full"}
																		maxWidth={38}
																		marginLeft="auto"
																	>
																		<Ellipsis color="#8298AB" />
																	</Box>
																</HStack>
															</Col>
														</Row>

														<Row style={{ paddingBottom: 6 }}>
															<Col style={{ flexDirection: "row", justifyContent: "space-between" }}>
																<HStack gap={4}>
																	<Text fontSize={12}>
																		{notification.eventType ? (
																			<HStack gap={8} alignItems="center">
																				<Text color={resolvedPrimarylight}>{iconMapping[notification.eventType]}</Text>
																				<Text sx={hasReadStyle}>{eventTypeLabels[notification.eventType]}</Text>
																			</HStack>
																		) : (
																			""
																		)}
																	</Text>
																</HStack>
															</Col>
														</Row>

														<Row style={{ paddingBottom: 6 }}>
															<Col style={{ flexDirection: "row", justifyContent: "space-between" }}>
																<HStack gap={4}>
																	<Text fontSize={12} sx={hasReadStyle}>
																		{notification.siteName ? notification.siteName : "..."}{" "}
																	</Text>
																</HStack>
															</Col>
														</Row>
													</Col>
												</Row>
											);
										}
										return (
											<Row style={styleData} key={notification.id}>
												<Col style={{ justifyContent: "center" }}>
													<HStack gap={8}>
														<FormattedDate sx={{ marginLeft: 18, ...hasReadStyle }} variant="DD/MMM" date={notification.dateCreated} />
													</HStack>
												</Col>

												<Col style={{ justifyContent: "center" }}>
													<HStack gap={8} alignItems="center">
														<Typography>
															{notification.eventType ? (
																<HStack gap={8} alignItems="center">
																	<Typography color={resolvedPrimarylight}>{iconMapping[notification.eventType]}</Typography>
																	<Typography sx={hasReadStyle}>{eventTypeLabels[notification.eventType]}</Typography>
																</HStack>
															) : (
																""
															)}
														</Typography>
													</HStack>
												</Col>

												<Col style={{ justifyContent: "center" }}>
													<HStack gap={8} alignItems="center">
														<Typography sx={hasReadStyle}>{notification.transactionNumber}</Typography>
													</HStack>
												</Col>

												<Col style={{ justifyContent: "center" }}>
													{notification.siteName && (
														<HStack gap={8}>
															<MapPin size={24} color={resolvedPrimarylight} />
															<Typography sx={hasReadStyle}>{truncateText(notification.siteName, 30)}</Typography>
														</HStack>
													)}
													{!notification.siteName && (
														<HStack gap={8}>
															<MapPin size={24} color={"#999999"} />
															<Typography color="#999999" sx={hasReadStyle}>
																Not available
															</Typography>
														</HStack>
													)}
												</Col>
											</Row>
										);
									}}
								></Link>
								{/* {renderMenu(notification, menuMapping)} */}
							</Box>
						);
					})}
				</>
			)}
		</GridContainer>
	);
};

// const renderMenu = (notification: any, menuMapping: { [key: string]: MenuOptionType[] }) => {
// 	const menuOptions = menuMapping[notification.eventType] || [];

// 	if (menuOptions.length === 0) {
// 		return null;
// 	}

// 	return (
// 		<Menu
// 			style={{ position: "absolute", top: 18, right: 16, shadowOpacity: 0 }}
// 			onSelect={(value) => {
// 				const selectedOption = menuOptions.find((option) => option.value === value);
// 				if (selectedOption) {
// 					selectedOption.action(notification);
// 				}
// 			}}
// 		>
// 			<MenuTrigger>
// 				<Box borderWidth={1} borderColor="#8298AB" alignItems="center" borderRadius={"$full"} justifyContent="center" bg="#E1E9F0" width={"$full"} minWidth={38} maxWidth={38} marginLeft="auto">
// 					<Ellipsis color="#8298AB" />
// 				</Box>
// 			</MenuTrigger>
// 			<MenuOptions
// 				customStyles={{
// 					optionsWrapper: {
// 						borderRadius: 8,
// 					},
// 					optionsContainer: {
// 						borderRadius: 8,
// 						marginTop: 30,
// 					},
// 				}}
// 			>
// 				{menuOptions.map((option) => (
// 					<MenuOption key={option.value} value={option.value}>
// 						<Box flexDirection="row" gap={8} paddingVertical={6} borderRadius={6} backgroundColor="#E1E9F0" alignItems="center" justifyContent="center">
// 							{option.icon}
// 							<Text>{option.label}</Text>
// 						</Box>
// 					</MenuOption>
// 				))}
// 			</MenuOptions>
// 		</Menu>
// 	);
// };

export default NotificationTable;
